import { useAuth } from "@/contexts/AuthContext";

/**
 * RBAC permission helpers (frontend gating).
 *
 * The source of truth is server-side enforcement; this only controls what the
 * UI shows. super_admin always passes. A user's `permissions` array comes from
 * /auth/me.
 */
export function usedPermissions(): Set<string> {
  const { user } = useAuth();
  return new Set(user?.permissions ?? []);
}

export function usePermission() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions;
  // Fail OPEN when we have no permission data yet (undefined) — never hide the
  // whole app while /me is loading. super_admin is always unrestricted.
  // Admins are unrestricted by default; a positively-present permission list
  // (a deliberately-scoped role) is still honoured for them.
  const noData = perms === undefined;
  const unrestricted = isSuper || noData || (isAdmin && (perms?.length ?? 0) === 0);
  const set = new Set(perms ?? []);

  /** Does the user have a specific permission key ("feature:action")? */
  const can = (key: string): boolean => unrestricted || set.has(key);

  /** Does the user have ANY of the given keys? */
  const canAny = (...keys: string[]): boolean => unrestricted || keys.some((k) => set.has(k));

  /** Can the user view a feature (any action on it counts as visible)? */
  const canViewFeature = (feature: string): boolean =>
    unrestricted || [...set].some((p) => p.startsWith(`${feature}:`));

  return { can, canAny, canViewFeature, isSuper, isAdmin };
}
